import axios from "./axiosInstance";
import config from "./config";

const BASE_URL = config.BASE_URL;

// Type Definitions
export interface BrowserInfo {
  userAgent: string;
  language: string;
  platform: string;
  screenWidth: number;
  screenHeight: number;
  windowWidth: number;
  windowHeight: number;
  timezone: string;
}

export interface ErrorLogRequest {
  errorMessage: string;
  errorCode?: string;
  pageUrl?: string;
  pageTitle?: string;
  actionContext?: string;
  componentName?: string;
  apiEndpoint?: string;
  apiMethod?: string;
  requestPayload?: Record<string, unknown>;
  responseStatus?: number;
  responseError?: Record<string, unknown>;
  stackTrace?: string;
  browserInfo?: BrowserInfo;
  additionalContext?: Record<string, unknown>;
  // User context (optional - will be filled by backend from auth)
  userId?: string;
  userEmail?: string;
  userRole?: string;
}

export interface ErrorLogResponse {
  success: boolean;
  message: string;
  data?: {
    errorLogId: string;
    createdAt: string;
  };
  error?: string;
}

/**
 * Get browser information for error context
 */
export const getBrowserInfo = (): BrowserInfo => {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

/**
 * Log an error to the central database
 *
 * @param token - Authentication token
 * @param errorData - Error details to log
 * @returns Promise with error log creation response
 */
export const logError = async (
  token: string | null,
  errorData: ErrorLogRequest
): Promise<ErrorLogResponse> => {
  try {
    // Add browser info if not already provided
    const dataWithBrowserInfo = {
      ...errorData,
      browserInfo: errorData.browserInfo || getBrowserInfo(),
      pageUrl: errorData.pageUrl || window.location.href,
      pageTitle: errorData.pageTitle || document.title,
    };

    const response = await axios.post(
      `${BASE_URL}/error-logs`,
      dataWithBrowserInfo,
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error: unknown) {
    // Silently fail - we don't want error logging to cause more errors
    return { success: false, message: 'Failed to log error' };
  }
};

/**
 * Link an error log to a support ticket
 *
 * @param token - Authentication token
 * @param errorLogId - The error log ID
 * @param supportTicketId - The support ticket ID
 * @returns Promise with link response
 */
export const linkErrorToTicket = async (
  token: string | null,
  errorLogId: string,
  supportTicketId: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axios.patch(
      `${BASE_URL}/error-logs/${errorLogId}/link-ticket`,
      { supportTicketId },
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error: unknown) {
    return { success: false, message: 'Failed to link error to ticket' };
  }
};
